/**
 *
 * AcgFlow
 *
 * @version 1.0.0
 * @author Bartolo Amico
 * @since 30-Dec-2022
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import PortalLoader from 'Components/PortalLoader'
import { getJWT, internalFederation, externalFederation, getAuth, retrieveCodeHash } from './utils'
import { generateSessId } from 'Utility/globalMethods'
import { Config } from 'Constants/config_env';
import PortalModal from 'Components/PortalModal'
import style from './AcgFlow.scss'
import PreLoad from './PreLoad'
import PortalFooter from '../PortalFooter/PortalFooter';


const validationSchema = Yup.object().shape({
  username: Yup.string().email('Insert a valid email').required('Required'),
  password: Yup.string().required('Required')
})

const AcgFlow = (props) => {
  const navigate = useNavigate();

  const [alert, setAlert] = useState(false)
  const [preload, setPreload] = useState(false)
  const [loginError, setError] = useState(false)
  const [isOpen, setOpen] = useState(true)
  const codeObj = JSON.parse(window.sessionStorage.getItem('codeHash'))

  function createForm(params) {
    const form = document.createElement('form');
    form.method = 'post';
    form.action = Config.federate_external_login;

    for (const key in params) {
      // eslint-disable-next-line no-prototype-builtins
      if (params.hasOwnProperty(key)) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
    }

    return form;
  }

  const getAccessToken = (cid, tru) => {
    setPreload(true)

    const { codeVerifier } = JSON.parse(window.sessionStorage.getItem('oauth2_params'))

    const urlencoded = new URLSearchParams()
    urlencoded.append('grant_type', 'authorization_code')
    urlencoded.append('code', codeObj.code)
    urlencoded.append('redirect_uri', tru)
    urlencoded.append('client_id', cid)
    urlencoded.append('code_verifier', codeVerifier)

    getJWT(urlencoded).then((r) => {
      if (r.response.status === 200) {
        generateSessId()
        window.sessionStorage.setItem('jwt', r.response.data.access_token)
        window.location.replace('/')
      } else {
        setAlert(true);
        setPreload(false);
      }
      window.sessionStorage.removeItem('oauth2_params')
      window.sessionStorage.removeItem('isAuthorized')
      window.sessionStorage.removeItem('codeHash')
    })
  }

  const form = useFormik({
    initialValues: {
      username: '',
      password: '',
      rememberMe: true
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const params = {
        username: values.username,
        password: values.password,
      };

      const form = createForm(params);

      setSubmitting(false);
      document.body.appendChild(form);
      form.submit();
    },
  });

  const { touched, errors, values, handleChange, handleBlur, handleSubmit, isValid } = form

  useEffect(() => {
    if (!props.logged && codeObj === null && window.sessionStorage.getItem('isAuthorized') === null) getAuth(props)
    window.sessionStorage.getItem('codeHash') === null ? retrieveCodeHash() : getAccessToken(props.clientId, props.redirectUri)
  if (window.sessionStorage.getItem('loginError') !== null) setError(true)
  }, [location.search])

  useEffect(() => {
    setTimeout(() => {
      setAlert(false)
    }, 2000)
  }, [alert])

  return (
    <>
      {loginError && <PortalModal hasOutcome outcome='failure' title={{ id: 't.global.badcred' }} buttons={[
        {
          contentText: { id: 't.global.close' },
          onClick: () => {window.sessionStorage.removeItem('loginError'); setOpen(false)}
        },
        ]} open={isOpen} />
      }
      {!props.logged && props.needPreLoad && window.sessionStorage.getItem('isAuthorized') === null
        ? props.customPreload
          ? props.customPreload
          : <PreLoad />
        : (
          <div className={style['acg-container']}>
					<div className={style['acg-contents']}>
						<h4 className={style.title}>Login to {`${props.title}`}</h4>
            		{ preload ? <PortalLoader /> : (
                  <>
						<form onSubmit={handleSubmit} className={style.form}>
							{alert && (
								<div className={style.alertBadge}>
									<p className={style.alertMessage}>Wrong Credentials</p>
								</div>
							)}
							<div className={style['form-group']}>
								<input
									className={style.input}
									type='text'
									id='username'
									name='username'
									label='Username'
									fullWidth
									value={values.username}
									onChange={handleChange}
									onBlur={handleBlur}
									error={touched.username && Boolean(errors.username)}
								/>
								<label className={style.labelInput} htmlFor='username'>
									Enter username
								</label>
							</div>
							<div className={style['form-group']}>
								<input
									className={style.input}
									type='password'
									id='password'
									name='password'
									label='Password'
									fullWidth
									value={values.password}
									onChange={handleChange}
									onBlur={handleBlur}
									error={touched.password && Boolean(errors.password)}
								/>
								<label className={style.labelInput} htmlFor='password'>
									Enter password
								</label>
							</div>
							<div className={style['acg-extra']}>
								{props.forgotPassword && (
									<div className={`${style.anchorContainer} ${style.forgotPassword}`}>
										<a
										  href='#'
										  className={style['forgot-pass']}
										  onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											navigate('/forgottenpassword');
										}}
										>
											Password Dimenticata?
										</a>
									</div>
								)}
								{props.rememberMe && (
									<label className={style.boxCheckbox}>
										<input
											className={style.htmlCheckbox}
											type='checkbox'
											id='rememberMe'
											name='rememberMe'
											value={values.rememberMe}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										<span className={style.checkbox}></span>
										<p className={style.label}>Remember me</p>
									</label>
								)}
							</div>
							<button type='submit' style={props.btnstyle} disabled={!isValid}>
								ACCEDI
							</button>
							<div className={style.anchorContainer}>
								<a
									className={style.anchor}
									href='#'
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										internalFederation();
									}}>
									Hai un account XCORP?
								</a>
							</div>
						</form>
          </>)}
					</div>
					<div className={style['acg-bg']} style={{ backgroundImage: `url(${props.background})` }} />
				</div>
      )}
      <PortalFooter />
    </>
  )
}


AcgFlow.propTypes = {
  // Add here some propTypes
  // and set a minimal description i.e.
  /** Description of prop "baz". */
  background: PropTypes.string,
  title: PropTypes.string,
  btnstyle: PropTypes.object,
  clientId: PropTypes.string,
  codeVerifier: PropTypes.string,
  clientSecret: PropTypes.string,
  tokenRedirectUri: PropTypes.string,
  needPreLoad: PropTypes.bool,
  logged: PropTypes.bool,
  rememberMe: PropTypes.bool,
  forgotPassword: PropTypes.bool,
  customPreload: PropTypes.node,
  redirectUri: PropTypes.string,
}

AcgFlow.defaultProps = {
  // Add here some default propTypes values
  background: '',
  title: '',
  btnstyle: { background: '#000080' },
  clientId: '',
  codeVerifier: '',
  clientSecret: '',
  tokenRedirectUri: '',
  needPreLoad: false,
  rememberMe: false,
  forgotPassword: false,
  logged: false,
  customPreload: undefined
}

export default AcgFlow
